import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { portalTranslations } from '../i18n/portalTranslations';
import { formatPortalDate, getShipmentByDeclaration, getStatusOrder } from '../services/shipmentService';
import { Shipment, ShipmentStatus } from '../types/shipment';
import './ShipmentTrackerDemo.css';

const statusIcons: Record<ShipmentStatus, string> = {
  arrived: '🚢',
  inspection: '🔍',
  clearance: '📋',
  release: '✅',
  delivered: '📦',
};

const COMPANY_WA = '963933612861';

const ShipmentTrackerDemo: React.FC = () => {
  const { language } = useLanguage();
  const pt = portalTranslations[language].tracker;
  const [declaration, setDeclaration] = useState('');
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [searching, setSearching] = useState(false);

  const steps = getStatusOrder();

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!declaration.trim()) return;
    setSearching(true);
    setNotFound(false);

    const found = await getShipmentByDeclaration(declaration.trim());
    setSearching(false);

    if (!found) {
      setNotFound(true);
      setShipment(null);
      return;
    }

    setShipment(found);
  };

  const currentIndex = shipment ? steps.indexOf(shipment.status) : -1;
  const historyByStatus = new Map((shipment?.history || []).map((h) => [h.status, h]));

  const waHelp = `https://wa.me/${COMPANY_WA}?text=${encodeURIComponent(
    language === 'ar'
      ? `مرحباً، أحتاج مساعدة بخصوص رقم البيان: ${declaration || '—'}`
      : `Hello, I need help with declaration: ${declaration || '—'}`
  )}`;

  return (
    <section id="tracker" className="tracker-section">
      <div className="container">
        <h2 className="section-title">{pt.title}</h2>
        <p className="section-subtitle">{pt.subtitle}</p>

        <form className="tracker-form" onSubmit={handleTrack}>
          <label htmlFor="declaration-input">{pt.declarationLabel}</label>
          <div className="tracker-form-row">
            <input
              id="declaration-input"
              type="text"
              value={declaration}
              onChange={(e) => setDeclaration(e.target.value)}
              placeholder={pt.declarationPlaceholder}
              dir="ltr"
              autoComplete="off"
            />
            <button type="submit" className="btn btn-primary" disabled={searching}>
              {searching ? (language === 'ar' ? 'جاري البحث...' : 'Searching...') : pt.trackButton}
            </button>
          </div>
          <p className="tracker-hint">{pt.hint}</p>
          <p className="tracker-hint">{pt.howToGet}</p>
        </form>

        {notFound && (
          <div className="tracker-error-box">
            <p className="tracker-error">{pt.notFound}</p>
            <div className="tracker-error-actions">
              <a href={waHelp} className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                {pt.contactWhatsApp}
              </a>
              <Link to="/portal" className="btn btn-primary">{pt.portalLink}</Link>
            </div>
          </div>
        )}

        {shipment && (
          <div className="tracker-result">
            <div className="tracker-result-header">
              <div>
                <p className="tracker-result-label">{pt.declarationLabel}</p>
                <p className="tracker-result-number" dir="ltr">{shipment.declarationNumber}</p>
              </div>
              <div className="tracker-result-status">
                <span className="portal-status-badge">{pt.steps[shipment.status]}</span>
                <p className="tracker-result-updated">
                  {pt.lastUpdate}: {formatPortalDate(shipment.updatedAt, language)}
                </p>
              </div>
            </div>
            <div className="tracker-meta">
              <span><strong>{language === 'ar' ? 'البضاعة' : 'Cargo'}:</strong> {shipment.cargoDescription}</span>
              <span><strong>{language === 'ar' ? 'المعبر' : 'Border'}:</strong> {shipment.border}</span>
              <span><strong>{pt.currentStatus}:</strong> {pt.steps[shipment.status]}</span>
            </div>
            <div className="tracker-timeline">
              {steps.map((step, index) => {
                const done = index <= currentIndex;
                const active = index === currentIndex;
                const event = historyByStatus.get(step);
                return (
                  <div
                    key={step}
                    className={`tracker-step ${done ? 'done' : ''} ${active ? 'active' : ''}`}
                  >
                    <span className="tracker-step-icon">{statusIcons[step]}</span>
                    <span className="tracker-step-label">{pt.steps[step]}</span>
                    {event?.at && (
                      <span className="tracker-step-date">{formatPortalDate(event.at, language)}</span>
                    )}
                    {event?.note && <span className="tracker-step-note">{event.note}</span>}
                    {active && <span className="tracker-step-pulse" aria-hidden="true" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="tracker-portal-cta">
          <Link to="/portal" className="btn btn-secondary">{pt.portalLink}</Link>
        </div>
      </div>
    </section>
  );
};

export default ShipmentTrackerDemo;
